import CanvasDragTarget, { DRAG_TARGET_TYPE } from './CanvasDragTarget';
import { measureFlatWaveRestBox, measureWaveCircleRestBox } from './visualizerRestBox';
import { toBaseUnits, toRendererBounds } from '../canvasElementSnapping';
import { CANVAS_ELEMENT_DRAG, VISUALIZER_SHAPE } from '../../PreviewDisplayConstants';

// The visualizer. There is only ever one, and both of its shapes store the same single position, so
// dragging either writes to visualizer.position exactly as the X and Y Position sliders do.
//
// Dragged only, never resized: the circle's size is the Radius control and the flat wave's is Width.
//
// Its box is the shape it has at rest, not what is drawn. The reasons are in visualizerRestBox; what
// follows from them is that the outline, the grab area and the lines other elements align to all hold
// still while the music plays.
export default class VisualizerDragTarget extends CanvasDragTarget {

    constructor(visualizer, onCommit) {
        super('visualizer', DRAG_TARGET_TYPE.VISUALIZER);

        this.visualizer = visualizer;
        this.onCommit = onCommit;
    }

    // The rest shape, in base units, with its middle measured from the visualizer's position. Null
    // while nothing of the visualizer is shown.
    measureRestBox() {
        throw new Error('measureRestBox must be implemented by the target');
    }

    // Placed on the resting position the renderer holds rather than on the stored one: a drag moves the
    // renderer every frame but writes the store only on release, and the outline has to travel with the
    // visualizer while it is carried. Bounce, shake and drift all offset from that resting position
    // without changing it, so none of them moves the box.
    //
    // Grown to the grab minimum, so the outline shown is also the area that picks the visualizer up,
    // and a flat wave with no base height still has an outline to show at all.
    measureBox() {
        const restBox = this.measureRestBox();

        if (!restBox)
            return null;

        const restingPosition = toBaseUnits({ x: this.visualizer.x, y: this.visualizer.y });
        const minimumHalfSize = CANVAS_ELEMENT_DRAG.MINIMUM_GRAB_SIZE * 0.5;

        return {
            centerX: restingPosition.x + restBox.centerX,
            centerY: restingPosition.y + restBox.centerY,
            halfWidth: Math.max(restBox.halfWidth, minimumHalfSize),
            halfHeight: Math.max(restBox.halfHeight, minimumHalfSize)
        };
    }

    getBounds() {
        return toRendererBounds(this.measureBox());
    }

    getPosition() {
        return { ...this.visualizer.config.position };
    }

    moveTo(position) {
        this.visualizer.moveTo(position);
    }

    commit(position) {
        this.onCommit({ position });
    }
}

export class FlatWaveDragTarget extends VisualizerDragTarget {

    measureRestBox() {
        return measureFlatWaveRestBox(this.visualizer.config);
    }
}

// The circle is grabbed as a disc rather than as a box, so the corners of its bounding square, which
// are canvas the user can see straight through, keep belonging to whatever is drawn behind them.
export class WaveCircleDragTarget extends VisualizerDragTarget {

    measureRestBox() {
        return measureWaveCircleRestBox(this.visualizer.config);
    }

    // The disc the outline is drawn around, so the circle is picked up by the same area whether the
    // music is loud, quiet or stopped.
    containsPoint(point) {
        const box = this.measureBox();

        if (!box)
            return false;

        const acrossX = point.x - box.centerX;
        const acrossY = point.y - box.centerY;

        return acrossX * acrossX + acrossY * acrossY <= box.halfWidth * box.halfWidth;
    }
}

export function createVisualizerDragTarget(visualizer, shape, onCommit) {
    if (shape === VISUALIZER_SHAPE.FLAT)
        return new FlatWaveDragTarget(visualizer, onCommit);

    return new WaveCircleDragTarget(visualizer, onCommit);
}
